import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import KpiCard from "@/components/KpiCard";
import StudentDashboard from "@/pages/StudentDashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  GraduationCap, Users, Wallet, CalendarCheck, BookOpenCheck, Megaphone, Sparkles,
  ArrowUpRight, TrendingUp, ShieldCheck, Heart,
} from "lucide-react";
import { Link } from "react-router-dom";

const zar = (n) => `R ${Number(n || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;
const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();

export default function Dashboard() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [children, setChildren] = useState([]);
  const isParent = user?.role === "parent";
  const isStudent = user?.role === "student";

  useEffect(() => {
    if (isStudent) return;
    (async () => {
      try {
        const promises = [
          api.get("/dashboard/overview"),
          api.get("/announcements"),
        ];
        if (isParent) promises.push(api.get("/parent/children"));
        const [ov, ann, kids] = await Promise.all(promises);
        setOverview(ov.data);
        setAnnouncements(ann.data.slice(0, 3));
        if (kids) setChildren(kids.data);
      } catch {}
    })();
  }, [isParent, isStudent]);

  // Students get a bespoke dashboard.
  if (isStudent) return <StudentDashboard />;

  const greet = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">// {user?.role} workspace</div>
          <h1 className="mt-1 text-4xl md:text-5xl font-bold tracking-tight">
            {greet()}, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="mt-1 text-muted-foreground font-serif-display">Here is the pulse of your school today.</p>
        </div>
        <div className="flex gap-2">
          <Badge className="rounded-full bg-primary/10 text-primary hover:bg-primary/15 border-primary/20">
            Term 1 · 2026
          </Badge>
          <Badge variant="outline" className="rounded-full">
            ZAR (R)
          </Badge>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KpiCard testId="kpi-students" label="Students" value={overview?.students ?? "—"} hint={`${overview?.classes ?? 0} active classes`} icon={GraduationCap} />
        <KpiCard testId="kpi-teachers" label="Teachers" value={overview?.teachers ?? "—"} hint="Staff on payroll" icon={Users} />
        <KpiCard testId="kpi-fees" label="Fees Collected" value={zar(overview?.fees_collected)} hint={`Arrears ${zar(overview?.fees_arrears)}`} icon={Wallet} accent />
        <KpiCard testId="kpi-attendance" label="Attendance today" value={`${overview?.present_today ?? 0}/${overview?.attendance_today ?? 0}`} hint="Present / Marked" icon={CalendarCheck} />
      </div>

      {isParent && children.length > 0 && (
        <Card className="rounded-2xl" data-testid="parent-children-widget">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><Heart className="h-4 w-4 text-primary" /> Your child{children.length > 1 ? "ren" : ""}</CardTitle>
            <Link to="/children" className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
              Full view <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2">
              {children.map((c) => (
                <Link key={c.id} to="/children" className="lift p-4 rounded-xl bg-muted/40 flex items-center gap-3">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback className="bg-primary/15 text-primary">{initials(c.name)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{c.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{c.class_name} · Grade {c.grade}</div>
                    <div className="flex gap-2 mt-1.5">
                      <Badge variant="outline" className="rounded-full text-[0.65rem]">
                        <CalendarCheck className="h-3 w-3 mr-1" /> {c.attendance_pct}% attendance
                      </Badge>
                      {c.fees_due > 0 && (
                        <Badge className="rounded-full bg-red-500/15 text-red-500 hover:bg-red-500/20 text-[0.65rem]">
                          {zar(c.fees_due)} due
                        </Badge>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2 rounded-2xl">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Fee book — Term 1</CardTitle>
            <Link to="/fees" className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
              View all <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "Collected", value: overview?.fees_collected, color: "bg-emerald-500/15 text-emerald-500" },
                { label: "Arrears", value: overview?.fees_arrears, color: "bg-red-500/15 text-red-500" },
                { label: "Pending", value: overview?.fees_pending, color: "bg-amber-500/15 text-amber-500" },
              ].map((r) => (
                <div key={r.label} className="p-4 rounded-xl bg-muted/40">
                  <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">{r.label}</div>
                  <div className="mt-2 text-2xl font-bold tracking-tight">{zar(r.value)}</div>
                  <div className={`mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.65rem] ${r.color}`}>
                    <TrendingUp className="h-3 w-3" /> live
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
              <Link to="/attendance" className="lift p-4 rounded-xl bg-muted/40 flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary grid place-items-center"><CalendarCheck className="h-5 w-5" /></div>
                <div>
                  <div className="text-sm font-medium">Mark attendance</div>
                  <div className="text-xs text-muted-foreground">Today's register</div>
                </div>
              </Link>
              <Link to="/homework" className="lift p-4 rounded-xl bg-muted/40 flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary grid place-items-center"><BookOpenCheck className="h-5 w-5" /></div>
                <div>
                  <div className="text-sm font-medium">Set homework</div>
                  <div className="text-xs text-muted-foreground">Assign & grade</div>
                </div>
              </Link>
              <Link to="/ai" className="lift p-4 rounded-xl bg-muted/40 flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary grid place-items-center"><Sparkles className="h-5 w-5" /></div>
                <div>
                  <div className="text-sm font-medium">AI assistant</div>
                  <div className="text-xs text-muted-foreground">Draft comments</div>
                </div>
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><Megaphone className="h-4 w-4 text-primary" /> Announcements</CardTitle>
            <Link to="/announcements" className="text-xs text-muted-foreground hover:text-foreground">All</Link>
          </CardHeader>
          <CardContent className="space-y-3">
            {announcements.length === 0 ? (
              <div className="text-sm text-muted-foreground">No announcements yet.</div>
            ) : (
              announcements.map((a) => (
                <div key={a.id} className="p-3 rounded-xl bg-muted/40">
                  <div className="text-sm font-medium">{a.title}</div>
                  <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{a.body}</div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-2xl border-dashed">
        <CardContent className="p-6 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
          <div className="flex gap-4 items-start">
            <div className="h-12 w-12 rounded-2xl bg-primary/15 text-primary grid place-items-center">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <div className="text-sm font-medium">Compliance — SASA & POPIA</div>
              <div className="text-xs text-muted-foreground max-w-2xl">
                School OS is built with the South African Schools Act (SASA) and POPIA in mind — role-based access, encrypted credentials, audit logs, and consent-first parent portals.
              </div>
            </div>
          </div>
          <Badge className="rounded-full bg-primary text-primary-foreground">Enterprise ready</Badge>
        </CardContent>
      </Card>
    </div>
  );
}
